import { useEffect, useRef, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { Check, Globe, Server, Smartphone } from "lucide-react";
import type { Platform } from "@metrickle/schema/constants";
import { authClient } from "../lib/auth";
import { resetIdentity, track } from "../lib/track";
import { Logo } from "./layout";
import { Button, SectionLabel, cx } from "./ui";

/** The three set-up steps, in order. A step outside first-run set-up (another workspace, another app) shows none. */
export const ONBOARDING_STEPS = ["Workspace", "Your app", "Install"] as const;

/**
 * The frame every set-up step shares, matching sign-up's (pages/auth.tsx AuthFrame): the form on
 * the left and a preview on the right that fills in as the person answers. Each page sets its own title.
 */
export function OnboardingFrame({
  step,
  title,
  subtitle,
  eyebrow,
  children,
  preview,
}: {
  /** 1–3 during first-run set-up; omit for "another workspace" or "another app". */
  step?: 1 | 2 | 3;
  title: string;
  subtitle: ReactNode;
  /** A line above the title, after "Step n of 3". */
  eyebrow?: ReactNode;
  children: ReactNode;
  preview: ReactNode;
}) {
  const session = authClient.useSession();
  const user = session.data?.user;
  const navigate = useNavigate();
  const titleRef = useRef<HTMLHeadingElement>(null);
  // Each step is its own route: move focus to the new heading, as a route change does elsewhere.
  useEffect(() => {
    titleRef.current?.focus({ preventScroll: true });
  }, [step]);

  const signOut = async () => {
    track("logout");
    await authClient.signOut();
    resetIdentity();
    navigate("/login");
  };

  return (
    <div className="grid min-h-dvh lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
      <div className="flex flex-col px-4 py-6 sm:px-10 sm:py-8">
        <div className="flex items-center justify-between gap-3">
          <Logo className="text-[17px]" />
          {user && <span className="min-w-0 truncate text-[13px] text-ink-3">{user.email}</span>}
        </div>
        <main className="@container flex flex-1 items-center justify-center py-12">
          <div className="auth-rise w-full max-w-[440px]">
            {step && (
              <>
                <ol aria-label="Set-up steps" className="mb-7 grid grid-cols-1 gap-1.5 @xs:grid-cols-3">
                  {ONBOARDING_STEPS.map((label, i) => {
                    const n = i + 1;
                    return (
                      <li
                        key={label}
                        aria-current={n === step ? "step" : undefined}
                        className={cx("flex min-w-0 flex-col gap-2 text-xs [overflow-wrap:anywhere]", n === step ? "font-semibold text-ink" : n < step ? "text-ink-2" : "text-ink-3")}
                      >
                        <span
                          aria-hidden
                          className={cx("h-1 rounded-full", n <= step ? "bg-accent forced-colors:bg-[CanvasText]" : "bg-surface-3 forced-colors:border forced-colors:border-[GrayText]")}
                        />
                        <span>
                          {n < step && <span className="sr-only">Done: </span>}
                          {label}
                        </span>
                      </li>
                    );
                  })}
                </ol>
                <p className="mb-1.5 text-[13px] text-ink-2">
                  Step {step} of {ONBOARDING_STEPS.length}
                  {eyebrow && <> · {eyebrow}</>}
                </p>
              </>
            )}
            <h1 ref={titleRef} tabIndex={-1} className="text-[28px] leading-tight font-semibold tracking-[-0.02em] text-balance text-ink outline-none @md:text-[32px]">
              {title}
            </h1>
            <p className="mt-2 text-[15px] text-ink-2">{subtitle}</p>
            <div className="mt-7">{children}</div>
          </div>
        </main>
        {user && (
          <p className="text-xs text-ink-3">
            Signed in as {user.name || user.email}.{" "}
            <Button variant="link" className="text-xs" onClick={() => void signOut()}>
              Sign out
            </Button>
          </p>
        )}
      </div>
      {preview}
    </div>
  );
}

/** One icon per platform, shared by the preview and set-up's platform choice. */
export const PLATFORM_ICON: Record<Platform, typeof Globe> = {
  web: Globe,
  ios: Smartphone,
  android: Smartphone,
  "react-native": Smartphone,
  flutter: Smartphone,
  server: Server,
};

/**
 * A small picture of the workspace being set up: the switcher, the app, the person and a checklist.
 * Hidden from assistive tech and described by the headline, like sign-up's product panel.
 */
export function OnboardingPreview({
  eyebrow,
  headline,
  step,
  workspace,
  otherWorkspace,
  app,
  firstEvent,
  current = "You're here",
}: {
  eyebrow: string;
  headline: ReactNode;
  step: 1 | 2 | 3;
  workspace: string;
  /** Shown under the new one when this is another workspace. */
  otherWorkspace?: string;
  app?: { name: string; platform: Platform };
  /** Set once the first event has arrived; the checklist completes and the feed shows it. */
  firstEvent?: { where: string; meta: string };
  /** What the current step says under its name. */
  current?: string;
}) {
  const session = authClient.useSession();
  const person = session.data?.user.name || session.data?.user.email || "You";
  const initials = person
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const done = (n: number) => n < step || (n === 3 && !!firstEvent);
  const Icon = app ? PLATFORM_ICON[app.platform] : Globe;
  const items = [
    { t: "Name the workspace", d: step > 1 ? workspace : current },
    { t: "Add your app", d: step === 2 ? current : step > 2 && app ? app.name : "Website, iOS, Android or Flutter" },
    { t: "Install and see your first visit", d: firstEvent ? "First visit arrived" : step === 3 ? "Waiting for your first visit" : "We'll tell you the moment it arrives" },
  ];

  return (
    <aside aria-label="Preview of your workspace" className="hidden p-3 lg:block">
      <div className="auth-panel relative flex h-full flex-col gap-10 overflow-hidden rounded-[28px] border border-line px-12 pt-16 pb-12 xl:px-16">
        <div className="max-w-md">
          <p className="text-[13px] font-medium tracking-wide text-accent-text uppercase">{eyebrow}</p>
          <p className="mt-3 text-[34px] leading-[1.1] font-semibold tracking-[-0.025em] text-balance text-ink xl:text-[40px]">{headline}</p>
        </div>

        <div aria-hidden className="auth-rise auth-delay-1 grid max-w-[540px] grid-cols-[180px_1fr] overflow-hidden rounded-2xl border border-line bg-surface shadow-[0_1px_2px_rgba(0,0,0,0.04),0_24px_48px_-24px_rgba(0,0,0,0.25)]">
          <div className="flex flex-col gap-3.5 border-r border-line bg-bg p-3">
            <div className={cx("flex items-center gap-2 rounded-lg border bg-surface p-1.5", step === 1 ? "border-accent ring-3 ring-accent-wash" : "border-line")}>
              {/* Not named yet: an empty dashed tile, not a "?" that reads as an error. */}
              <span
                className={cx(
                  "grid size-[22px] shrink-0 place-items-center rounded-md text-[11px] font-semibold",
                  workspace ? "bg-accent text-on-accent" : "border border-dashed border-control",
                )}
              >
                {workspace.trim()[0]?.toUpperCase()}
              </span>
              <span className={cx("truncate text-[13px] font-semibold", !workspace && "font-medium text-ink-3")}>{workspace || "Your workspace"}</span>
            </div>
            {otherWorkspace && (
              <div className="flex items-center gap-2 px-1.5 text-xs text-ink-3">
                <span className="grid size-[18px] shrink-0 place-items-center rounded bg-surface-3 text-[11px] text-ink-2">{otherWorkspace[0]?.toUpperCase()}</span>
                <span className="truncate">{otherWorkspace}</span>
              </div>
            )}
            <SectionLabel className="px-1.5">Apps</SectionLabel>
            {app ? (
              <div className={cx("flex min-w-0 items-center gap-2 rounded-lg border bg-surface p-1.5 text-xs font-semibold", step === 2 ? "border-accent ring-3 ring-accent-wash" : "border-line")}>
                <span className="grid size-[18px] shrink-0 place-items-center rounded bg-accent text-on-accent">
                  <Icon size={11} />
                </span>
                <span className="truncate">{app.name || "Your app"}</span>
                {firstEvent && (
                  <span className="ml-auto inline-flex shrink-0 items-center gap-1 text-[11px] text-good">
                    <span className="size-1.5 rounded-full bg-good" />
                    Live
                  </span>
                )}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-control p-1.5 text-xs text-ink-3">+ Add an app</div>
            )}
            <SectionLabel className="px-1.5">People</SectionLabel>
            <div className="flex items-center gap-2 px-1.5 text-xs text-ink-2">
              <span className="grid size-[18px] shrink-0 place-items-center rounded-full bg-accent text-[11px] font-semibold text-on-accent">{initials}</span>
              <span className="truncate">{person} (you)</span>
            </div>
          </div>
          <div className="flex min-w-0 flex-col gap-2 p-4">
            <p className="mb-1 truncate text-[15px] font-semibold text-ink">Setting up {workspace || "your workspace"}</p>
            {items.map((it, i) => {
              const n = i + 1;
              const now = n === step && !done(n);
              return (
                <div key={it.t} className={cx("flex items-start gap-2.5 rounded-lg border px-3 py-2.5 text-[13px]", now ? "border-accent bg-accent-wash" : "border-line")}>
                  <span
                    className={cx(
                      "grid size-5 shrink-0 place-items-center rounded-full border-[1.5px] text-[11px] font-semibold",
                      done(n) ? "border-good bg-good text-on-accent" : now ? "border-accent text-accent-text" : "border-control text-ink-3",
                    )}
                  >
                    {done(n) ? <Check size={12} strokeWidth={3} /> : n}
                  </span>
                  <span className="min-w-0">
                    <span className={cx("block font-semibold", done(n) ? "text-ink-2" : "text-ink")}>{it.t}</span>
                    <span className="block truncate text-ink-3">{it.d}</span>
                  </span>
                </div>
              );
            })}
            {firstEvent && (
              <div className="mt-1 flex flex-col gap-1.5 rounded-lg border border-line px-3 py-2.5">
                <span className="flex items-center gap-1.5 text-xs font-semibold text-good">
                  <Check size={12} strokeWidth={3} />1 visit so far
                </span>
                <span className="flex justify-between gap-2 text-xs text-ink-2">
                  <span className="truncate font-mono text-ink">{firstEvent.where}</span>
                  <span className="shrink-0">{firstEvent.meta}</span>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
}
